import React, { useEffect, useState, useMemo } from 'react';
import { useDuckDB } from '../context/LazyDuckDBContext';
import { FancyLoader } from './feedback/FancyLoader';
import { isMobileBrowser } from '../utils/isMobileBrowser';
import { MUNICIPALITY_BOUNDARIES_TABLE } from '../services/useSqlRooms';

interface MunicipalitySilhouetteProps {
  kommunId: string;
  className?: string;
  variant?: "card" | "hero";
}

interface MunicipalityGeo {
  municipality_id: string;
  municipality_name: string;
  boundary_json: string;
  bbox_south: number;
  bbox_north: number;
  bbox_west: number;
  bbox_east: number;
}

export const MunicipalitySilhouette: React.FC<MunicipalitySilhouetteProps> = ({
  kommunId,
  className = "",
  variant = "card"
}) => {
  const { query, dataVersion, ensureMunicipalityBoundaries } = useDuckDB();
  const [geos, setGeos] = useState<MunicipalityGeo[]>([]);
  const [loading, setLoading] = useState(false);
  const [countyName, setCountyName] = useState<string>("");
  const isHero = variant === "hero";

  useEffect(() => {
    let cancelled = false;
    async function loadGeo() {
      // Pad to 4 digits if needed (e.g. "128" -> "0128")
      let idStr = String(kommunId);
      if (!idStr || idStr === 'null') return;
      if (idStr.length === 3) idStr = '0' + idStr;

      setLoading(true);
      try {
        await ensureMunicipalityBoundaries();
        // Safe check if geo table exists using duckdb_tables() to avoid console noise
        const check = await query<{ cnt: number }[]>(
          "SELECT COUNT(*) as cnt FROM duckdb_tables() WHERE table_name = 'dim_municipality_geo'"
        );
        if (!check || check.length === 0 || Number(check[0].cnt) === 0) {
          if (!cancelled) setLoading(false);
          return;
        }

        const mobile = isMobileBrowser();
        const geoFrom = mobile
          ? `dim_municipality_geo g INNER JOIN ${MUNICIPALITY_BOUNDARIES_TABLE} b ON b.municipality_id = g.municipality_id`
          : 'dim_municipality_geo';
        const boundaryCol = mobile ? 'b.boundary_json' : 'boundary_json';
        const bboxCols = mobile
          ? 'g.bbox_south, g.bbox_north, g.bbox_west, g.bbox_east'
          : 'bbox_south, bbox_north, bbox_west, bbox_east';
        const idCol = mobile ? 'g.municipality_id' : 'municipality_id';
        const nameCol = mobile ? 'g.municipality_name' : 'municipality_name';
        const boundaryPred = mobile ? 'b.boundary_json IS NOT NULL' : 'boundary_json IS NOT NULL';
        const idFilter = mobile ? 'g.municipality_id' : 'municipality_id';
        const likeFilter = mobile ? 'g.municipality_id' : 'municipality_id';

        // 1. Get the target municipality and its county context
        const targetResult = await query<MunicipalityGeo[]>(
          `SELECT ${idCol} AS municipality_id, ${nameCol} AS municipality_name, ${boundaryCol} AS boundary_json, ${bboxCols}
           FROM ${geoFrom}
           WHERE ${idFilter} = :id AND ${boundaryPred}
           LIMIT 1`,
          { id: idStr }
        );

        if (cancelled || !targetResult || targetResult.length === 0) {
          if (!cancelled) setLoading(false);
          return;
        }

        const countyPrefix = idStr.substring(0, 2);

        // Fetch all municipalities in the same county
        const countyResult = await query<MunicipalityGeo[]>(
          `SELECT ${idCol} AS municipality_id, ${nameCol} AS municipality_name, ${boundaryCol} AS boundary_json, ${bboxCols}
           FROM ${geoFrom}
           WHERE ${likeFilter} LIKE :prefix AND ${boundaryPred}`,
          { prefix: `${countyPrefix}%` }
        );

        // County name - hardcoded mapping since county_codes table doesn't exist in WASM
        const countyNames: Record<string, string> = {
          '01': 'Stockholms län', '03': 'Uppsala län', '04': 'Södermanlands län',
          '05': 'Östergötlands län', '06': 'Jönköpings län', '07': 'Kronobergs län',
          '08': 'Kalmar län', '09': 'Gotlands län', '10': 'Blekinge län',
          '12': 'Skåne län', '13': 'Hallands län', '14': 'Västra Götalands län',
          '17': 'Värmlands län', '18': 'Örebro län', '19': 'Västmanlands län',
          '20': 'Dalarnas län', '21': 'Gävleborgs län', '22': 'Västernorrlands län',
          '23': 'Jämtlands län', '24': 'Västerbottens län', '25': 'Norrbottens län'
        };
        const cName = countyNames[countyPrefix] || "Län";

        if (!cancelled) {
          setGeos(countyResult && countyResult.length > 0 ? countyResult : targetResult);
          setCountyName(cName);
        }
      } catch (err) {
        // Only log if it's not a "table doesn't exist" error
        const errStr = String(err);
        if (!errStr.includes('does not exist') && !errStr.includes('no such table')) {
          console.error('Failed to load municipality silhouette:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadGeo();
    return () => { cancelled = true; };
  }, [kommunId, query, dataVersion, ensureMunicipalityBoundaries]);

  const mapData = useMemo(() => {
    if (geos.length === 0) return null;

    try {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      geos.forEach(g => {
        minX = Math.min(minX, g.bbox_west);
        maxX = Math.max(maxX, g.bbox_east);
        minY = Math.min(minY, g.bbox_south);
        maxY = Math.max(maxY, g.bbox_north);
      });

      const dx = maxX - minX;
      const dy = maxY - minY;

      // Use a scale factor to get reasonable coordinate values
      const scaleFactor = 1000;
      const countyWidth = dx * scaleFactor;
      const countyHeight = dy * scaleFactor;

      // Normalize the kommunId for comparison
      const normalizedKommunId = String(kommunId).replace(/^0+/, '');
      const activeGeo = geos.find(g => String(g.municipality_id).replace(/^0+/, '') === normalizedKommunId);

      // Calculate the center of the active municipality in our scaled space
      let centerX = countyWidth / 2;
      let centerY = countyHeight / 2;

      if (activeGeo) {
        const activeCenterX = (activeGeo.bbox_west + activeGeo.bbox_east) / 2;
        const activeCenterY = (activeGeo.bbox_south + activeGeo.bbox_north) / 2;
        centerX = (activeCenterX - minX) * scaleFactor;
        centerY = (maxY - activeCenterY) * scaleFactor;
      }

      // In hero mode, we center the viewBox on the active municipality.
      const viewBox = isHero
        ? `${centerX - countyWidth / 2} ${centerY - countyHeight / 2} ${countyWidth} ${countyHeight}`
        : `0 0 ${countyWidth} ${countyHeight}`;

      const paths = geos.map(geo => {
        const boundary = JSON.parse(geo.boundary_json);
        const polygons = boundary.type === 'Polygon' ? [boundary.coordinates] : boundary.coordinates;

        const path = polygons.map((rings: any) => {
          return rings.map((ring: any) => {
            const points = ring.map((coord: [number, number]) => {
              const x = (coord[0] - minX) * scaleFactor;
              const y = (maxY - coord[1]) * scaleFactor;
              return `${x.toFixed(2)},${y.toFixed(2)}`;
            });
            return `M ${points.join(' L ')} Z`;
          }).join(' ');
        }).join(' ');

        const normalizedGeoId = String(geo.municipality_id).replace(/^0+/, '');
        const isActive = normalizedGeoId === normalizedKommunId;

        return {
          id: geo.municipality_id,
          path,
          isActive
        };
      });

      return { paths, viewBox, viewBoxWidth: countyWidth, viewBoxHeight: countyHeight };
    } catch (e) {
      return null;
    }
  }, [geos, kommunId, isHero]);

  if (loading || !mapData) {
    return (
      <div className={`flex items-center justify-center w-full h-full min-h-[200px] ${className}`}>
        <FancyLoader label={null} sizePx={isHero ? 80 : 40} />
      </div>
    );
  }

  if (isHero) {
    // Calculate stroke width relative to viewBox size for consistent appearance
    const baseStroke = Math.min(mapData.viewBoxWidth, mapData.viewBoxHeight) * 0.003;

    return (
      <>
        {/* Main Hero Art - SVG fills entire parent via absolute positioning */}
        <svg
          viewBox={mapData.viewBox}
          className={`absolute inset-0 w-full h-full block transition-opacity duration-700 ${className}`}
          preserveAspectRatio="xMidYMid slice"
          role="img"
          aria-label={countyName}
        >
          {/* Render inactive municipalities first (below) */}
          {mapData.paths.filter(item => !item.isActive).map(item => (
            <path
              key={item.id}
              d={item.path}
              fill="#64748b"
              fillOpacity="0.08"
              stroke="#94a3b8"
              strokeWidth={baseStroke}
              strokeLinejoin="round"
              className="transition-all duration-500"
            />
          ))}
          {/* Render active municipality on top with strong highlight */}
          {mapData.paths.filter(item => item.isActive).map(item => (
            <path
              key={item.id}
              d={item.path}
              fill="#10b981"
              fillOpacity="0.45"
              stroke="#10b981"
              strokeWidth={baseStroke * 6}
              strokeLinejoin="round"
              className="transition-all duration-500"
            />
          ))}
        </svg>

        {/* Minimal floating annotation - absolute positioned corner */}
        <div className="absolute bottom-4 left-4 z-20 pointer-events-none">
          <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/40 dark:bg-slate-900/40 backdrop-blur-md border border-white/20 dark:border-white/10 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-[10px] font-black text-slate-700 dark:text-slate-200 uppercase tracking-widest leading-none">
              {countyName}
            </span>
          </div>
        </div>
      </>
    );
  }

  // Standard Card view
  const cardStroke = Math.min(mapData.viewBoxWidth, mapData.viewBoxHeight) * 0.004;

  return (
    <div className={`relative flex items-center justify-center shrink-0 w-full h-full ${className}`}>
      <svg
        viewBox={mapData.viewBox}
        className="w-full h-full block drop-shadow-sm transition-opacity duration-500 overflow-visible"
        preserveAspectRatio="xMidYMid meet"
      >
        {mapData.paths.map(item => (
          <path
            key={item.id}
            d={item.path}
            fill={item.isActive ? '#10b981' : '#94a3b8'}
            fillOpacity={item.isActive ? '0.12' : '0.05'}
            stroke={item.isActive ? '#10b981' : '#cbd5e1'}
            strokeWidth={item.isActive ? cardStroke * 3 : cardStroke}
            strokeLinejoin="round"
            className="transition-all duration-300"
          />
        ))}
      </svg>
    </div>
  );
};

export default MunicipalitySilhouette;

